import { useRenderNotifier } from '@wui/common-library'
import throttle from 'lodash.throttle'
import { defineStore } from 'pinia'
import { computed, ref, triggerRef, watch } from 'vue'

export * from './speakers'

import {
  detachBackgroundBlurProcessor,
  reapplyBackgroundBlurOnActiveSessions,
  requestKeyframeAfterReplace,
  stopIdleBackgroundBlurPipelines,
} from '@/features/background-blur'
import { deactivateLocalVideoStream } from '@/features/call-session-media'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useWebRTC } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { LocalStorageKey } from '@/shared/types/store'
import { sendDTMFMediaConstraintsToSession } from '@/shared/utils/jssip'
import {
  ensureLocalScreenShareEncoding,
  getScreenShareVideoConstraints,
  parseScreenShareQualityId,
  type ScreenShareQualityId,
} from '@/shared/utils/webrtc'

import { getAudioInputConstraints } from '../lib/get-audio-input-constraints'
import { getWebcamVideoConstraints, parseWebcamQualityId, type WebcamQualityId } from '../lib/get-webcam-video-constraints'
import { isDisplayMediaTrack } from '../lib/is-display-media-track'
import { isLiveMediaTrackFromDevice } from '../lib/is-live-media-track-from-device'
import { isLocalMicCaptureReleased } from '../lib/is-local-mic-capture-released'
import { DefineActions, DeviceId, MediaDevice } from '../types'

const getValueFromLocalStorage = (key: LocalStorageKey) => {
  const value = localStorage.getItem(key)
  if (value) {
    return JSON.parse(value)
  }
  return null
}

const saveToLocalStorage = (key: LocalStorageKey, deviceId: string) => {
  localStorage.setItem(key, JSON.stringify(deviceId))
}

type StoredDeviceKind = 'audioInput' | 'audioOutput' | 'videoInput'
type StoredDeviceMap = {
  [k in StoredDeviceKind]: Map<DeviceId, MediaDevice>
}

const applyVideoContentHint = (stream: MediaStream, hint: 'detail' | 'motion') => {
  if (!stream || typeof stream.getVideoTracks !== 'function') {
    return
  }

  const [videoTrack] = stream.getVideoTracks()
  if (videoTrack) {
    videoTrack.contentHint = hint
  }
}

export const useStoreMediaDevices = defineStore('mediaDevices', () => {
  const { notifyError, notifySuccess, notifyWarning } = useRenderNotifier()
  const { t } = useLocalization()
  const { sessions } = useWebRTC()

  const activeMicrophoneId = ref('')
  const activeWebcamId = ref('')
  const webcamQuality = ref<WebcamQualityId>(parseWebcamQualityId(getValueFromLocalStorage('webcamQuality')))
  const screenShareQuality = ref<ScreenShareQualityId>(parseScreenShareQualityId(getValueFromLocalStorage('screenShareQuality')))
  const activeSpeakerId = ref('')
  const activeIncomingCallSpeakerId = ref('')
  const incomingCallSpeakerVolume = ref(1)
  const devices = ref<StoredDeviceMap>({
    audioInput: new Map() as Map<DeviceId, MediaDevice>,
    audioOutput: new Map() as Map<DeviceId, MediaDevice>,
    videoInput: new Map() as Map<DeviceId, MediaDevice>,
  })
  const _prevDevices = ref<StoredDeviceMap>({
    audioInput: new Map() as Map<DeviceId, MediaDevice>,
    audioOutput: new Map() as Map<DeviceId, MediaDevice>,
    videoInput: new Map() as Map<DeviceId, MediaDevice>,
  })
  const _streams = ref(new Map<MediaStream['id'], MediaStream>())
  const _deviceStoreIsEmpty = ref(true)

  const isMicrophoneAvailable = computed(() => {
    return !![...devices.value.audioInput.values()].filter(({ deviceId }) => !!deviceId).length
  })
  const isWebcamAvailable = computed(() => {
    return !![...devices.value.videoInput.values()].filter(({ deviceId }) => !!deviceId).length
  })
  const isSpeakerAvailable = computed(() => {
    return !![...devices.value.audioOutput.values()].filter(({ deviceId }) => !!deviceId).length
  })

  const isMicrophonePermissionGranted = ref(false)
  const isCameraPermissionGranted = ref(false)
  const microphoneAccessError = ref<DOMException | null>(null)
  const cameraAccessError = ref<DOMException | null>(null)

  const _savePrevDevices = () => {
    _prevDevices.value.audioInput.clear()
    _prevDevices.value.audioOutput.clear()
    _prevDevices.value.videoInput.clear()
    for (const [key, value] of devices.value.audioInput) {
      _prevDevices.value.audioInput.set(key, value)
    }
    for (const [key, value] of devices.value.audioOutput) {
      _prevDevices.value.audioOutput.set(key, value)
    }
    for (const [key, value] of devices.value.videoInput) {
      _prevDevices.value.videoInput.set(key, value)
    }
  }

  const _fillDevicesFromEnumeration = (enumeratedDevices: MediaDeviceInfo[]) => {
    _deviceStoreIsEmpty.value = false
    _savePrevDevices()
    devices.value.audioInput.clear()
    devices.value.audioOutput.clear()
    devices.value.videoInput.clear()

    enumeratedDevices.forEach((device) => {
      const deviceForSet = {
        deviceId: device.deviceId,
        groupId: device.groupId,
        kind: device.kind,
        label: device.label,
      }
      if (device.kind === 'audiooutput') {
        devices.value.audioOutput.set(device.deviceId, deviceForSet)
      } else if (device.kind === 'audioinput') {
        devices.value.audioInput.set(device.deviceId, deviceForSet)
      } else if (device.kind === 'videoinput') {
        devices.value.videoInput.set(device.deviceId, device)
      }
    })

    triggerRef(devices)
  }

  const _resolveActiveDeviceIds = (preserveExisting = false) => {
    const microphone = getValueFromLocalStorage('microphoneId')
    if (!preserveExisting || !activeMicrophoneId.value || !devices.value.audioInput.has(activeMicrophoneId.value)) {
      if (microphone && devices.value.audioInput.has(microphone)) {
        activeMicrophoneId.value = microphone
      } else if (devices.value.audioInput.size) {
        const nextMicrophone = devices.value.audioInput.values().next().value?.deviceId ?? ''
        activeMicrophoneId.value = nextMicrophone
        saveToLocalStorage('microphoneId', nextMicrophone)
      }
    }

    const webcam = getValueFromLocalStorage('webcamId')
    if (!preserveExisting || !activeWebcamId.value || !devices.value.videoInput.has(activeWebcamId.value)) {
      if (webcam && devices.value.videoInput.has(webcam)) {
        activeWebcamId.value = webcam
      } else if (devices.value.videoInput.size) {
        const nextWebcam = devices.value.videoInput.values().next().value?.deviceId ?? ''
        activeWebcamId.value = nextWebcam
        saveToLocalStorage('webcamId', nextWebcam)
      } else if (!preserveExisting) {
        activeWebcamId.value = ''
      }
    }

    const speaker = getValueFromLocalStorage('speakerId')
    if (!preserveExisting || !activeSpeakerId.value || !devices.value.audioOutput.has(activeSpeakerId.value)) {
      if (speaker && devices.value.audioOutput.has(speaker)) {
        activeSpeakerId.value = speaker
      } else if (devices.value.audioOutput.size) {
        const nextSpeaker = devices.value.audioOutput.values().next().value?.deviceId ?? ''
        activeSpeakerId.value = nextSpeaker
        saveToLocalStorage('speakerId', nextSpeaker)
      }
    }

    const incomingCallSpeaker = getValueFromLocalStorage('incomingCallSpeakerId')
    if (!preserveExisting || !activeIncomingCallSpeakerId.value || !devices.value.audioOutput.has(activeIncomingCallSpeakerId.value)) {
      if (incomingCallSpeaker && devices.value.audioOutput.has(incomingCallSpeaker)) {
        activeIncomingCallSpeakerId.value = incomingCallSpeaker
      } else if (devices.value.audioOutput.size) {
        const nextSpeaker = devices.value.audioOutput.values().next().value?.deviceId ?? ''
        activeIncomingCallSpeakerId.value = nextSpeaker
        saveToLocalStorage('incomingCallSpeakerId', nextSpeaker)
      }
    }

    const volume = getValueFromLocalStorage('incomingCallSpeakerVolume')
    if (volume) {
      incomingCallSpeakerVolume.value = volume
    }
  }

  const $init = async () => {
    try {
      isMicrophonePermissionGranted.value = (await navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor)).state === 'granted'
      isCameraPermissionGranted.value = (await navigator.permissions.query({ name: 'camera' } as unknown as PermissionDescriptor)).state === 'granted'
    } catch {}

    const enumeratedDevices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices()
    if (!enumeratedDevices.filter((info) => !!info.deviceId).length) {
      _deviceStoreIsEmpty.value = true
      return
    }
    _fillDevicesFromEnumeration(enumeratedDevices)
    _resolveActiveDeviceIds(false)
  }

  const syncDevicesAfterPermission = async () => {
    const enumeratedDevices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices()
    if (!enumeratedDevices.filter((info) => !!info.deviceId).length) {
      return
    }
    _fillDevicesFromEnumeration(enumeratedDevices)
    _resolveActiveDeviceIds(true)
  }

  navigator.mediaDevices.addEventListener('devicechange', throttle(async () => {
    await $init()
    const microphoneAction = _defineListenerAction('audioInput')
    if (microphoneAction) {
      if (microphoneAction.action === 'connect') {
        notifySuccess(t('common.micConnected', { mic: microphoneAction.device.label }))

      } else if (microphoneAction.action === 'disconnect' && isMicrophoneAvailable.value) {
        notifyWarning(t('common.micDisconnected', { mic: microphoneAction.device.label, now: devices.value.audioInput.get(activeMicrophoneId.value)?.label ?? 'default' }))
        await replaceMicrophoneOnActiveSessions()

      } else if (microphoneAction.action === 'disconnect' && !isMicrophoneAvailable.value) {
        notifyError(t('common.micDisconnectedNotAvailable', { mic: microphoneAction.device.label }))
      }
    }
    const videoAction = _defineListenerAction('videoInput')
    if (videoAction) {
      if (videoAction.action === 'connect') {
        notifySuccess(t('common.webcamConnected', { cam: videoAction.device.label }))
      } else if (videoAction.action === 'disconnect' && isWebcamAvailable.value) {
        notifyWarning(t('common.webcamDisconnected', { cam: videoAction.device.label, now: devices.value.videoInput.get(activeWebcamId.value)?.label ?? 'default' }))
      } else if (videoAction.action === 'disconnect' && !isWebcamAvailable.value) {
        notifyError(t('common.webcamDisconnectedNotAvailable', { mic: videoAction.device.label }))
      }
    }
    const speakerAction = _defineListenerAction('audioOutput')
    if (speakerAction) {
      if (speakerAction.action === 'connect') {
        notifySuccess(t('common.speakerConnected', { spk: speakerAction.device.label }))

      } else if (speakerAction.action === 'disconnect' && isSpeakerAvailable.value) {
        notifyWarning(t('common.speakerDisconnected', { spk: speakerAction.device.label, now: devices.value.audioOutput.get(activeSpeakerId.value)?.label ?? 'default' }))

      } else if (speakerAction.action === 'disconnect' && !isSpeakerAvailable.value) {
        notifyError(t('common.speakerisconnectedNotAvailable', { spk: speakerAction.device.label }))
      }
    }
  }, 1000))

  const _defineListenerAction = (kind: StoredDeviceKind): DefineActions | undefined => {
    const targetDevicesSize = devices.value[kind].size
    const prevDevicesSize = _prevDevices.value[kind].size
    if (targetDevicesSize === prevDevicesSize) return
    if (targetDevicesSize > prevDevicesSize) {
      for (const [key, value] of devices.value[kind]) {
        if (!_prevDevices.value[kind].has(key)) {
          return {
            action: 'connect',
            deviceId: key,
            device: value,
          }
        }
      }
    } else {
      for (const [key, value] of _prevDevices.value[kind]) {
        if (!devices.value[kind].has(key)) {
          return {
            action: 'disconnect',
            deviceId: key,
            device: value,
          }
        }
      }
    }
    return
  }

  const closeStream = (stream: MediaStream) => {
    const rawStream = detachBackgroundBlurProcessor(stream)
    const tracks = stream.getTracks()
    tracks.forEach((track) => {
      track.stop()
    })
    _streams.value.delete(stream.id)

    if (rawStream && rawStream.id !== stream.id) {
      rawStream.getTracks().forEach((track) => {
        track.stop()
      })
      _streams.value.delete(rawStream.id)
    }
  }

  /**
   * Stop orphan blur keep-alives / idle preview tracks that lock the physical camera.
   * Pipelines published on an active call session are preserved.
   */
  const releaseIdleLocalVideoStreams = () => {
    const sessionVideoIds = new Set(
      Array.from(sessions?.value.values() ?? [])
        .map((facade) => facade.localVideoStream.value?.id)
        .filter((id): id is string => Boolean(id)),
    )

    stopIdleBackgroundBlurPipelines(sessionVideoIds)

    for (const stream of [..._streams.value.values()]) {
      if (!stream.getVideoTracks().length) continue
      if (sessionVideoIds.has(stream.id)) continue
      closeStream(stream)
    }
  }

  const getSessionAudioTrack = (sessionFacade: {
    localAudioStream: { value: MediaStream | undefined }
    session: { connection?: RTCPeerConnection }
  }): MediaStreamTrack | undefined => {
    const fromStream = sessionFacade.localAudioStream.value?.getAudioTracks()[0]
    if (fromStream) {
      return fromStream
    }

    const connection = sessionFacade.session.connection
    if (!connection || connection.connectionState === 'closed') {
      return undefined
    }

    return connection.getSenders().find((sender) => sender.track?.kind === 'audio')?.track ?? undefined
  }

  const getSessionWebcamTrack = (sessionFacade: {
    localVideoStream: { value: MediaStream | undefined }
  }): MediaStreamTrack | undefined => {
    const track = sessionFacade.localVideoStream.value?.getVideoTracks()[0]
    if (!track || isDisplayMediaTrack(track)) {
      return undefined
    }

    return track
  }

  const isSessionEligibleForMicHotplug = (sessionFacade: {
    localAudioStream: { value: MediaStream | undefined }
    sessionId: string
    callId: { value: string }
    isOnHold: { value: boolean }
    isMuted: { value: boolean }
  }) => {
    if (!sessionFacade.localAudioStream.value) {
      return false
    }

    return !isLocalMicCaptureReleased(sessionFacade)
  }

  const replaceMicrophoneOnActiveSessions = async (
    audioDeviceId?: DeviceId,
    options?: { force?: boolean },
  ): Promise<void> => {
    try {
      const targetDeviceId = audioDeviceId ?? activeMicrophoneId.value
      const knownAudioDevices = Array.from(devices.value.audioInput.values())
      const sessionsList = Array.from(sessions.value.values())
      const targetSessions = options?.force
        ? sessionsList
        : sessionsList.filter(isSessionEligibleForMicHotplug)

      if (!targetSessions.length) {
        return
      }

      const alreadyUsingDevice = targetSessions.every((sessionFacade) => {
        const track = getSessionAudioTrack(sessionFacade)
        return isLiveMediaTrackFromDevice(track, targetDeviceId, knownAudioDevices)
      })

      if (alreadyUsingDevice) {
        return
      }

      const stream = await openMicrophone(audioDeviceId)
      const audioTrack = stream.getAudioTracks()[0]
      targetSessions.forEach((sessionFacade) => {
        const prevAudioStream = sessionFacade.localAudioStream.value
        if (prevAudioStream) {
          closeStream(prevAudioStream)
        }
        sessionFacade.localAudioStream.value = stream
        triggerRef(sessionFacade.localAudioStream)
        const connection = sessionFacade.session.connection as RTCPeerConnection
        if (connection && connection.connectionState !== 'closed' && audioTrack) {
          audioTrack.enabled = true
          const audioSender = connection.getSenders().find((sender) => sender.track?.kind === 'audio')
            ?? connection.getTransceivers?.().find((transceiver) => (
              transceiver.sender.track?.kind === 'audio'
              || transceiver.receiver?.track?.kind === 'audio'
            ))?.sender

          if (audioSender) {
            void audioSender.replaceTrack(audioTrack)
          }
        }
      })
    } catch {
      notifyError(t('common.failedToUseMicVideo'))
    }
  }

  const _replaceWebcamOnActiveSessions = async (videoDeviceId?: DeviceId, force = false) => {
    try {
      const targetDeviceId = videoDeviceId ?? activeWebcamId.value
      const knownVideoDevices = Array.from(devices.value.videoInput.values())
      const sessionsList = Array.from(sessions?.value.values() ?? [])
      // Only sessions that already publish webcam. Screen-only sessions must not
      // open a camera or replaceTrack into the display-media sender.
      const sessionsWithWebcam = sessionsList.filter((sessionFacade) => {
        const track = getSessionWebcamTrack(sessionFacade)
        return track?.readyState === 'live'
      })

      if (!sessionsWithWebcam.length) {
        return
      }

      const alreadyUsingDevice = sessionsWithWebcam.every((sessionFacade) => {
        const track = getSessionWebcamTrack(sessionFacade)
        return isLiveMediaTrackFromDevice(track, targetDeviceId, knownVideoDevices)
      })

      if (alreadyUsingDevice && !force) {
        return
      }

      // Detach blur + stop old camera before openWebcam so keep-alive / restore
      // do not block getUserMedia (common on Windows). Remember sender track ids
      // while the blurred canvas may still be on the PeerConnection.
      const prevSenderTrackIdBySession = new Map<string, string>()
      for (const sessionFacade of sessionsWithWebcam) {
        const webcamTrack = getSessionWebcamTrack(sessionFacade)
        if (webcamTrack?.id) {
          prevSenderTrackIdBySession.set(sessionFacade.sessionId, webcamTrack.id)
        }

        const currentStream = sessionFacade.localVideoStream.value
        if (!currentStream) continue
        const restoreStream = detachBackgroundBlurProcessor(currentStream)
        const streamToRelease = restoreStream ?? currentStream
        if (restoreStream && restoreStream.id !== currentStream.id) {
          sessionFacade.localVideoStream.value = restoreStream
          triggerRef(sessionFacade.localVideoStream)
        }
        closeStream(streamToRelease)
      }

      const stream = await openWebcam(targetDeviceId)
      const videoTrack = stream.getVideoTracks()[0]
      for (const sessionFacade of sessionsWithWebcam) {
        sessionFacade.localVideoStream.value = stream
        triggerRef(sessionFacade.localVideoStream)
        const connection = sessionFacade.session.connection as RTCPeerConnection
        if (!connection || connection.connectionState === 'closed' || !videoTrack) {
          continue
        }

        const prevTrackId = prevSenderTrackIdBySession.get(sessionFacade.sessionId)
        const senders = connection.getSenders()
        const webcamSender = (prevTrackId
          ? senders.find((sender) => sender.track?.id === prevTrackId)
          : undefined)
          ?? senders.find((sender) => {
            const track = sender.track
            return !!track && track.kind === 'video' && !isDisplayMediaTrack(track)
          })

        if (webcamSender) {
          await webcamSender.replaceTrack(videoTrack)
          await requestKeyframeAfterReplace(webcamSender, { retryMs: 250 })
        }
      }

      // Raw camera is on the sender; restore blur preference if enabled.
      void reapplyBackgroundBlurOnActiveSessions().catch((error) => {
        console.warn('[media-devices] reapply blur after webcam replace failed', error)
      })
    } catch {
      sessions?.value.forEach((facade) => {
        deactivateLocalVideoStream(facade.localVideoStream, facade.session.connection as RTCPeerConnection)
        facade.isVideoMuted.value = true
        triggerRef(facade.localVideoStream)
        const room = useCallSessionRoomStore().getRoom(facade.sessionId)
        if (room?.isGroup) {
          const internalNumber = useCurrentUser().currentUser.value?.internalNumber
          if (internalNumber) {
            useRoomControl().memberMuteVideo(room.pServedUser, internalNumber, facade.callId.value)
          }
        } else {
          sendDTMFMediaConstraintsToSession(
            facade.session,
            { video: false },
          )
        }
      })
    }
  }

  /**
   * Registers stream only. Do NOT call $init() here — it re-assigns active device ids
   * and, via watchers, can trigger a second getUserMedia after openMedia / grantMicrophonePermission.
   * Post-permission list refresh: syncDevicesAfterPermission from openMicrophone / openWebcam / openMedia
   * (settings page also requests media via onMounted → open*, not only via probe*).
   * Hotplug refresh stays on the throttled `devicechange` listener.
   * Do NOT infer mic/camera permission from tracks here — openDisplay also uses this path
   * and its tracks are not microphone/webcam permissions.
   */
  const _getUserMediaProxy = (stream: MediaStream): MediaStream => {
    _streams.value.set(stream.id, stream)
    return stream
  }

  const markMicrophonePermissionGranted = () => {
    isMicrophonePermissionGranted.value = true
  }

  const markCameraPermissionGranted = () => {
    isCameraPermissionGranted.value = true
  }

  const openMicrophone = async (deviceId: string = activeMicrophoneId.value): Promise<MediaStream> => {
    const audio = getAudioInputConstraints(deviceId, devices.value.audioInput.values())
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio }).then(_getUserMediaProxy)
      markMicrophonePermissionGranted()
      microphoneAccessError.value = null
      // Settings page mounts testAudioInput → openMicrophone (not probe*). After first grant
      // devicechange often does not fire — refresh list here when still empty.
      if (!isMicrophoneAvailable.value || !isSpeakerAvailable.value) {
        await syncDevicesAfterPermission()
      }
      return stream
    } catch (e) {
      microphoneAccessError.value = e instanceof DOMException ? e : null
      throw e
    }
  }

  const openMedia = async (audio: boolean, video: boolean): Promise<MediaStream> => {
    if (video) {
      releaseIdleLocalVideoStreams()
    }
    const audioDevice = audio && getAudioInputConstraints(activeMicrophoneId.value, devices.value.audioInput.values())
    const videoDevice = video && getWebcamVideoConstraints(
      webcamQuality.value,
      activeWebcamId.value,
      devices.value.videoInput.has(activeWebcamId.value),
    )
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: audioDevice,
      video: videoDevice,
    }).then((mediaStream) => {
      if (audio) {
        markMicrophonePermissionGranted()
      }
      if (video) {
        markCameraPermissionGranted()
      }
      applyVideoContentHint(mediaStream, 'motion')
      return _getUserMediaProxy(mediaStream)
    })
    const needsAudioList = audio && (!isMicrophoneAvailable.value || !isSpeakerAvailable.value)
    const needsVideoList = video && !isWebcamAvailable.value
    if (needsAudioList || needsVideoList) {
      await syncDevicesAfterPermission()
    }

    return stream
  }

  type OpenWebcamOptions = {
    applyEffects?: boolean
  }

  const openWebcam = async (
    deviceId: string = activeWebcamId.value,
    options: OpenWebcamOptions = {},
  ): Promise<MediaStream> => {
    void options
    const videoDevice = getWebcamVideoConstraints(
      webcamQuality.value,
      deviceId,
      devices.value.videoInput.has(deviceId),
    )
    const constraints: MediaStreamConstraints = {
      video: videoDevice,
    }

    const tryGetUserMedia = () => navigator.mediaDevices.getUserMedia(constraints).then((mediaStream) => {
      applyVideoContentHint(mediaStream, 'motion')
      return _getUserMediaProxy(mediaStream)
    })

    try {
      // Drop orphan blur keep-alives / idle preview tracks that lock the physical camera.
      releaseIdleLocalVideoStreams()
      let stream: MediaStream
      try {
        stream = await tryGetUserMedia()
      } catch (firstError) {
        // One recovery pass after force-release (common after blur Timeout on Windows).
        releaseIdleLocalVideoStreams()
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 300)
        })
        try {
          stream = await tryGetUserMedia()
        } catch {
          throw firstError
        }
      }
      markCameraPermissionGranted()
      cameraAccessError.value = null
      if (!isWebcamAvailable.value) {
        await syncDevicesAfterPermission()
      }
      // Blur must not run inside openWebcam — it holds the device during MediaPipe init.
      return stream
    } catch (e) {
      console.error(e)
      cameraAccessError.value = e instanceof DOMException ? e : null
      throw e
    }
  }

  const probeMicrophoneAccess = async (): Promise<boolean> => {
    try {
      const stream = await openMicrophone()
      closeStream(stream)
      return true
    } catch {
      return false
    }
  }

  const probeCameraAccess = async (): Promise<boolean> => {
    try {
      const stream = await openWebcam(activeWebcamId.value, { applyEffects: false })
      closeStream(stream)
      return true
    } catch {
      return false
    }
  }

  const refreshWebcamOnActiveSessions = async () => {
    await _replaceWebcamOnActiveSessions(activeWebcamId.value, true)
  }

  const openDisplay = async (): Promise<MediaStream> => {
    return new Promise((resolve, reject) => {
      if (!navigator.mediaDevices.getDisplayMedia) {
        reject(new Error('Ваше устройство не поддерживает функцию getDisplayMedia'))
        return
      }
      navigator.mediaDevices.getDisplayMedia({
        video: getScreenShareVideoConstraints(screenShareQuality.value),
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      }).then((stream) => {
        // Prefer sharp text/UI over temporal smoothness (pairs with maintain-resolution on sender)
        applyVideoContentHint(stream, 'detail')
        return _getUserMediaProxy(stream)
      })
        .then(resolve)
        .catch(reject)
    })
  }

  watch(() => sessions.value.size, (value, oldValue) => {
    if (oldValue === 1 && value === 0) {
      if (!_streams.value.size) return
      _streams.value.forEach((stream) => {
        closeStream(stream)
      })
      _streams.value.clear()
    }
  })

  watch(activeMicrophoneId, () => {
    saveToLocalStorage('microphoneId', activeMicrophoneId.value)
    if (sessions.value.size) {
      replaceMicrophoneOnActiveSessions(activeMicrophoneId.value)
    }
  })

  watch(activeWebcamId, () => {
    saveToLocalStorage('webcamId', activeWebcamId.value)
    if (sessions.value.size) {
      _replaceWebcamOnActiveSessions(activeWebcamId.value)
    }
  })

  watch(webcamQuality, (quality) => {
    saveToLocalStorage('webcamQuality', quality)
    if (sessions.value.size) {
      void _replaceWebcamOnActiveSessions(activeWebcamId.value, true)
    }
  })

  watch(screenShareQuality, (quality) => {
    saveToLocalStorage('screenShareQuality', quality)
    sessions.value.forEach((sessionFacade) => {
      const stream = sessionFacade.localScreenStream.value
      const track = stream?.getVideoTracks().at(0)
      if (!track || track.readyState !== 'live') {
        return
      }

      void track.applyConstraints(getScreenShareVideoConstraints(quality)).catch(() => undefined)
      ensureLocalScreenShareEncoding(
        sessionFacade.session.connection as RTCPeerConnection,
        stream,
        quality,
      )
    })
  })

  watch(activeIncomingCallSpeakerId, () => {
    saveToLocalStorage('incomingCallSpeakerId', activeIncomingCallSpeakerId.value)
  })

  watch(activeSpeakerId, () => {
    saveToLocalStorage('speakerId', activeSpeakerId.value)
  })

  watch(incomingCallSpeakerVolume, (v) => {
    saveToLocalStorage('incomingCallSpeakerVolume', v.toString())
  })

  $init()

  return {
    activeMicrophoneId,
    activeWebcamId,
    webcamQuality,
    screenShareQuality,
    activeSpeakerId,
    activeIncomingCallSpeakerId,
    devices,
    isMicrophoneAvailable,
    isWebcamAvailable,
    isSpeakerAvailable,
    isMicrophonePermissionGranted,
    isCameraPermissionGranted,
    microphoneAccessError,
    cameraAccessError,
    incomingCallSpeakerVolume,
    closeStream,
    replaceMicrophoneOnActiveSessions,
    refreshWebcamOnActiveSessions,
    openDisplay,
    openMedia,
    openMicrophone,
    openWebcam,
    probeMicrophoneAccess,
    probeCameraAccess,
  }
})
